/**
 * FormField — one input within a FormWidget. Discriminated union by
 * `type`. Submissions are stored as JSON (key/value where key is
 * field.name and value is the raw input) and emailed via the homelab
 * SMTP relay per PLAN.md "Form-submission email notifications".
 */
import * as Schema from "effect/Schema";

/** A submitted field's key: what the server (or a GET query) receives. */
export const FieldName = Schema.String.pipe(
  Schema.pattern(/^[a-zA-Z][a-zA-Z0-9_]*$/, {
    message: () =>
      "Field name must start with a letter, then alphanumeric or underscore",
  }),
);

const FormFieldBase = {
  name: FieldName,
  label: Schema.String,
  required: Schema.optional(Schema.Boolean),
  placeholder: Schema.optional(Schema.String),
} as const;

export const TextFormField = Schema.Struct({
  type: Schema.Literal("text"),
  ...FormFieldBase,
});
export type TextFormField = Schema.Schema.Type<typeof TextFormField>;

export const EmailFormField = Schema.Struct({
  type: Schema.Literal("email"),
  ...FormFieldBase,
});
export type EmailFormField = Schema.Schema.Type<typeof EmailFormField>;

export const TextareaFormField = Schema.Struct({
  type: Schema.Literal("textarea"),
  ...FormFieldBase,
  rows: Schema.optional(Schema.Int.pipe(Schema.positive())),
});
export type TextareaFormField = Schema.Schema.Type<typeof TextareaFormField>;

export const CheckboxFormField = Schema.Struct({
  type: Schema.Literal("checkbox"),
  ...FormFieldBase,
  defaultChecked: Schema.optional(Schema.Boolean),
});
export type CheckboxFormField = Schema.Schema.Type<typeof CheckboxFormField>;

export const FormField = Schema.Union(
  TextFormField,
  EmailFormField,
  TextareaFormField,
  CheckboxFormField,
);
export type FormField = Schema.Schema.Type<typeof FormField>;
